/** Small pieces every reports screen shares: load/fail states, freshness words, in-app links from API hrefs. */
import { EmptyState, ErrorBanner, SkeletonRows } from '@patchbay/ui'
import type { UseQueryResult } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import type * as React from 'react'
import { ApiError, describeError } from '../../lib/api/client'
import type { FactRunOut, ReportSource } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'

/** Loading / failed / ready. A failed read is a banner with Retry, never an empty table or a zero (hard rule 10). */
export function QueryView<T>({
  query,
  what,
  children,
  rows = 6,
}: {
  query: UseQueryResult<T>
  what: string
  children: (data: T) => React.ReactNode
  rows?: number
}) {
  if (query.isPending) return <SkeletonRows count={rows} />
  if (query.isError || query.data === undefined) return <QueryError error={query.error} what={what} onRetry={() => void query.refetch()} />
  return <>{children(query.data)}</>
}

export function QueryError({ error, what, onRetry }: { error: unknown; what: string; onRetry?: () => void }) {
  if (error instanceof ApiError && error.status === 403) {
    return <EmptyState title={`${what} is not available to your role.`} detail="Ask a manager if you need it." />
  }
  if (error instanceof ApiError && error.notBuilt) {
    return <EmptyState title={`${what} is still being built.`} detail="It appears here as soon as it ships." />
  }
  return (
    <ErrorBanner
      title={`${what} could not load`}
      detail={`${describeError(error)}. Nothing is shown rather than a figure we cannot stand behind.`}
      onRetry={onRetry}
    />
  )
}

/** "2:50 PM" today, "Oct 12, 2:50 PM" otherwise, in Toronto time (#1231). */
export function whenLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'never'
  if (Number.isNaN(new Date(iso).getTime())) return 'unknown'
  const today = formatWhen(iso, { style: 'date', now }) === formatWhen(now.toISOString(), { style: 'date', now })
  return formatWhen(iso, { style: today ? 'time' : 'absolute', now })
}

/** A calendar day ("Oct 12", with the year when it isn't this year). A bare YYYY-MM-DD is that day in Toronto. */
export function dateLabel(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.length === 10 ? `${iso}T17:00:00Z` : iso)
  return Number.isNaN(d.getTime()) ? iso : formatWhen(d.toISOString(), { style: 'date' })
}

export const PREPARING = 'Reports are being prepared. The first build runs hourly.'

/** The Reports home freshness line from the catalogue's fact run. */
export function freshnessLine(run: FactRunOut | null | undefined): { text: string; tone: 'ok' | 'warn' | 'bad' } {
  if (!run) return { text: PREPARING, tone: 'warn' }
  if (run.state === 'failed') {
    return run.last_ok_at
      ? { text: `Couldn't refresh: facts last built ${whenLabel(run.last_ok_at)}`, tone: 'bad' }
      : { text: `Couldn't build the facts yet (${run.error ?? 'error'}). ${PREPARING}`, tone: 'bad' }
  }
  if (run.state === 'running') {
    return run.last_ok_at
      ? { text: `Facts as of ${whenLabel(run.last_ok_at)} · refreshing now`, tone: 'ok' }
      : { text: PREPARING, tone: 'warn' }
  }
  return { text: `Facts as of ${whenLabel(run.last_ok_at ?? run.finished_at)}`, tone: 'ok' }
}

const SOURCE_STATE: Record<ReportSource['state'], string> = {
  ok: 'ok',
  stale: 'stale',
  unavailable: 'unavailable',
  not_connected: 'not connected yet',
}

/** "Case facts: as of 14:50", "Desk mirror: ok", "Profitability: unavailable (reason)". */
export function sourceLine(source: ReportSource): string {
  const name = source.name.charAt(0).toUpperCase() + source.name.slice(1)
  const state = source.state === 'ok' && source.as_of ? `as of ${whenLabel(source.as_of)}` : SOURCE_STATE[source.state]
  const asOf = source.state !== 'ok' && source.as_of ? `, last good ${whenLabel(source.as_of)}` : ''
  return `${name}: ${state}${asOf}${source.note ? ` (${source.note})` : ''}`
}

/** An in-app link from an API `href` (it may carry a query string, which a typed `<Link to>` does not take). */
export function AppLink({ href, children, className }: { href: string; children: React.ReactNode; className?: string }) {
  const router = useRouter()
  const external = /^https?:\/\//.test(href)
  return (
    <a
      href={href}
      className={className}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      onClick={(event) => {
        if (external || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          return
        event.preventDefault()
        void router.navigate({ href })
      }}
    >
      {children}
    </a>
  )
}
